import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { ArrowLeft, ArrowRight, Target, Clock, DollarSign, TrendingUp, ShieldAlert, Briefcase, PiggyBank, GraduationCap, Globe, Banknote, BarChart3, LineChart, Wallet, Landmark, Heart, Coins, AlertCircle, Building2, Home, Umbrella, Star } from 'lucide-react';

const investmentQuestions = [
  {
    key: 'purpose',
    label: 'What is the main purpose of this investment?',
    icon: Target,
    options: [
      { value: 'emergency_fund', label: 'Emergency fund' },
      { value: 'short_term_savings', label: 'Short-term savings' },
      { value: 'specific_purchase', label: 'Saving for a specific purchase' },
      { value: 'retirement', label: 'Retirement planning' },
      { value: 'tax_free_savings', label: 'Tax-free savings' },
      { value: 'wealth_diversification', label: 'Wealth diversification' },
      { value: 'income_generation', label: 'Income generation' },
      { value: 'education', label: 'Education funding' },
      { value: 'estate_planning', label: 'Estate planning' },
      { value: 'offshore_diversification', label: 'Offshore diversification' },
      { value: 'other', label: 'Other' }
    ]
  },
  {
    key: 'time_horizon',
    label: 'How long do you plan to keep this money invested?',
    icon: Clock,
    options: [
      { value: 'less_than_1_year', label: 'Less than 1 year' },
      { value: '1_to_3_years', label: '1 to 3 years' },
      { value: '3_to_5_years', label: '3 to 5 years' },
      { value: '5_to_10_years', label: '5 to 10 years' },
      { value: 'more_than_10_years', label: 'More than 10 years' },
      { value: 'unsure', label: 'Unsure' }
    ]
  },
  {
    key: 'liquidity_need',
    label: 'Will you need access to this money in the near future?',
    icon: DollarSign,
    options: [
      { value: 'immediate_access', label: 'Yes, I may need access at short notice' },
      { value: 'partial_access', label: 'Yes, but only a portion' },
      { value: 'no_access_needed', label: 'No, I do not expect to need access soon' },
      { value: 'unsure', label: 'I am unsure' }
    ]
  },
  {
    key: 'expected_withdrawals',
    label: 'Do you plan to make withdrawals from this investment?',
    icon: DollarSign,
    options: [
      { value: 'no_withdrawals', label: 'No withdrawals planned' },
      { value: 'once_off', label: 'Once-off withdrawal expected' },
      { value: 'regular_monthly', label: 'Regular monthly withdrawals' },
      { value: 'irregular', label: 'Irregular withdrawals may be needed' },
      { value: 'unsure', label: 'Unsure' }
    ]
  },
  {
    key: 'current_investments',
    label: 'Do you currently have any investments?',
    icon: TrendingUp,
    options: [
      { value: 'first_time', label: 'This is my first time investing' },
      { value: 'have_investments', label: 'I already have investments' },
      { value: 'retirement_only', label: 'I have retirement savings only' },
      { value: 'discretionary_only', label: 'I have discretionary investments only' },
      { value: 'both', label: 'I have both retirement and discretionary investments' },
      { value: 'unsure', label: 'I am unsure' }
    ]
  },
  {
    key: 'experience',
    label: 'How would you describe your investment experience?',
    icon: TrendingUp,
    options: [
      { value: 'no_knowledge', label: 'I do not know anything about investing' },
      { value: 'some_research', label: 'I have done some research and want to learn more' },
      { value: 'some_experience', label: 'I have some experience with investing' },
      { value: 'experienced', label: 'I am an experienced investor' },
      { value: 'highly_experienced', label: 'I am a highly experienced investor' }
    ]
  },
  {
    key: 'stated_risk_profile',
    label: 'What risk profile do you consider yourself to be?',
    icon: ShieldAlert,
    options: [
      { value: 'conservative', label: 'Conservative' },
      { value: 'moderate_conservative', label: 'Moderate Conservative' },
      { value: 'moderate', label: 'Moderate' },
      { value: 'moderate_aggressive', label: 'Moderate Aggressive' },
      { value: 'aggressive', label: 'Aggressive' },
      { value: 'unsure', label: 'I do not know' }
    ]
  },

];

const iconMap = { Target, Clock, DollarSign, TrendingUp, ShieldAlert, Briefcase, PiggyBank, GraduationCap, Globe, Banknote, BarChart3, LineChart, Wallet, Landmark, Heart, Coins, AlertCircle, Building2, Home, Umbrella, Star };

const InvestmentQuestions = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { theme } = useTheme();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const answersRef = useRef({});
  const submittingRef = useRef(false);
  const advancingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await api.get('/investment-goal-rules/questions');
        if (res.data && res.data.length > 0) {
          const mapped = res.data.map(q => ({
            key: q.key,
            label: q.label,
            icon: iconMap[q.icon] || Target,
            options: q.answers.map(a => ({ value: a.value, label: a.label }))
          }));
          setQuestions(mapped);
        } else {
          setQuestions(investmentQuestions);
        }
      } catch (e) {
        setQuestions(investmentQuestions);
      } finally {
        setFetchLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  // Clamp the displayed index so a momentary overshoot during the final
  // submit transition can never render an undefined question (white screen).
  const safeIndex = questions.length > 0 ? Math.min(currentIndex, questions.length - 1) : 0;
  const currentQuestion = questions[safeIndex];
  const Icon = currentQuestion?.icon || Target;
  const progress = ((safeIndex + 1) / questions.length) * 100;

  const submitAnswers = async (finalAnswers) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setSubmitError('');
    try {
      const answerArray = Object.entries(finalAnswers).map(([key, value]) => ({
        question_key: key,
        answer_value: value
      }));

      await api.post(`/assessments/${id}/investment-answers`, { answers: answerArray });
      navigate(`/assessment/${id}/personality-assessment`);
    } catch (error) {
      console.error('Failed to submit answers:', error);
      setSubmitError(error.response?.data?.error || error.message || 'Failed to submit answers');
      submittingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (value) => {
    if (submittingRef.current || advancingRef.current) return;

    const updated = { ...answersRef.current, [currentQuestion.key]: value };
    answersRef.current = updated;
    setAnswers(updated);

    if (currentIndex < questions.length - 1) {
      advancingRef.current = true;
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        advancingRef.current = false;
      }, 200);
    } else {
      submitAnswers(updated);
    }
  };

  const handleNext = () => {
    if (submittingRef.current) return;

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      submitAnswers(answersRef.current);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  if (fetchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.surface }}>
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-current border-t-transparent rounded-full animate-spin" style={{ color: theme.primary }}></div>
          <p className="mt-4 text-sm" style={{ color: theme.textSecondary }}>Loading questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span style={{ color: theme.textSecondary }}>Question {currentIndex + 1} of {questions.length}</span>
            <span style={{ color: theme.primary }}>{Math.round(progress)}% complete</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: theme.border }}>
            <div
              className="h-full transition-all duration-300 rounded-full"
              style={{ width: `${progress}%`, backgroundColor: theme.primary }}
            />
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-lg" style={{ backgroundColor: `${theme.primary}20` }}>
                <Icon className="w-6 h-6" style={{ color: theme.primary }} />
              </div>
              <h1 className="text-xl font-bold" style={{ color: theme.text }}>
                {currentQuestion.label}
              </h1>
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-3 mb-8">
              {currentQuestion.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  disabled={loading}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    answers[currentQuestion.key] === option.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  style={{
                    backgroundColor: answers[currentQuestion.key] === option.value ? '' : theme.card,
                    borderColor: answers[currentQuestion.key] === option.value ? theme.primary : theme.border
                  }}
                >
                  <span className="font-medium" style={{ color: theme.text }}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>

            {submitError && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{submitError}</p>}

            <div className="flex justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!answers[currentQuestion.key] || loading}
              >
                {loading ? 'Submitting...' : currentIndex === questions.length - 1 ? 'Submit' : 'Next'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InvestmentQuestions;
